import { useEffect } from 'react';
import { usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import type { IAhuBoardTunnel, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { cmsBaseList } from '../../forms/cmsDoc';
import { catalogLabel } from '../../forms/formUtils';

/**
 * Hybrid AHU "Base" panel (§7.3 item 4a, tab.lifting.lug.base.tmpl) — split
 * out of the combined non-hybrid "Base & Lugs" collapse for hybrid AHUs
 * (legacy: `#lifting-lug-base-wrapper-tab`, its own sibling accordion item
 * labelled "Base"). Just the base select — AHU-level lug settings and the
 * per-lug editor live in the sibling Lifting Lugs panel (LiftingLugPane).
 *
 * `activeTunnelId` follows the same source LiftingLugPane uses for hybrid
 * AHUs (the currently selected canvas section's tunnel) — legacy instead
 * lets you click a specific base directly on the canvas, which the 3D canvas
 * port (Phase 9) will wire up; until then this uses the section selection,
 * consistent with every other Data Bar panel.
 *
 * Changing the base here is never confined to `activeTunnel`: the server
 * propagates `base_id` to every tunnel sharing the base and reports that set
 * as `same_base_tunnel_ids`. So a save highlights those bases on the canvas
 * (`highlight:tunnel:bases`) — otherwise the one visible edit quietly
 * re-bases tunnels the user never selected. The highlight clears when the
 * selection moves to another tunnel, when the panel collapses, and on
 * unmount. No legacy counterpart: Backbone serves the field and ignores it.
 */

interface BasePanelProps {
  tunnels: IAhuBoardTunnel[];
  activeTunnelId: number | null;
  cms: ICmsAhuDoc | undefined;
  open: boolean;
}

const BasePanel = ({ tunnels, activeTunnelId, cms, open }: BasePanelProps) => {
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const activeTunnel = tunnels.find((t) => t.id === activeTunnelId);

  // The highlight belongs to one save on one tunnel: drop it as soon as the
  // panel is showing something else, or nothing at all.
  useEffect(() => {
    ahuBoardBus.emit('highlight:tunnel:bases', []);
  }, [activeTunnelId, open]);
  useEffect(() => () => ahuBoardBus.emit('highlight:tunnel:bases', []), []);

  if (!open) {
    return (
      <ul id="lifting-lug-base-wrapper-tab" className="sublist lifting-lug-wrapper-tab">
        <li className="spinner-wrapper small">
          <div className="preloader dotted-preloader active" />
        </li>
      </ul>
    );
  }

  return (
    <ul id="lifting-lug-base-wrapper-tab" className="sublist lifting-lug-wrapper-tab">
      <li className="always-visible">
        <div className="single-line">
          <select
            name="base_id"
            data-tunnel={activeTunnel?.id}
            required
            className="tunnel_settings_field"
            value={activeTunnel?.base_id ?? ''}
            disabled={!activeTunnel}
            onChange={(e) => {
              if (!activeTunnel) return;
              patchTunnel({
                id: activeTunnel.id,
                ahuId: activeTunnel.ahu_id,
                data: { base_id: e.currentTarget.value ? Number(e.currentTarget.value) : null },
              })
                .unwrap()
                .then((saved) => {
                  // From the response, not the cached row: the peer set is
                  // recomputed after the save, and the list re-GET that
                  // refreshes the peers' own `base_id` is still in flight.
                  ahuBoardBus.emit('highlight:tunnel:bases', [
                    saved.id,
                    ...(saved.same_base_tunnel_ids ?? []),
                  ]);
                })
                .catch(() => undefined);
            }}
          >
            <option value="">- -</option>
            {cmsBaseList(cms).map((b) => (
              <option key={b.id} value={b.id}>
                {catalogLabel(b)}
              </option>
            ))}
          </select>
          <span className="label">Base</span>
        </div>
      </li>
      {/* Legacy (tab.lifting.lug.base.tmpl) renders a "Click on a specific
          base on the Canvas, in order to select it." hint row here. Dropped
          deliberately on request -- a considered deviation from parity, not
          an oversight. */}
    </ul>
  );
};

export default BasePanel;
